// src/pages/actividades/components/ActividadDnDList.tsx
import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Check, Play, Pause, Repeat, Calendar, Clock,
  ChevronRight, ChevronDown, CornerDownRight, User,
  Indent, Outdent, Star,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { StatusBadge, PriorityBadge } from '@/components/ui';
import type { ActividadListItem, PrioridadActividad } from '@/types';
import { SubtareaInlineAdd } from './SubtareaInlineAdd';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActividadDnDListProps {
  items: ActividadListItem[];
  onReorder: (items: { id: string; orden: number }[]) => void;
  onReparent: (id: string, padreId: string | null) => void;
  onComplete: (id: string) => void;
  onReopen: (id: string) => void;
  onStartTimer: (id: string) => void;
  onPauseTimer?: () => void;
  onResumeTimer?: () => void;
  isTimerPaused?: boolean;
  onUpdatePriority?: (id: string, prioridad: PrioridadActividad) => void;
  onToggleFoco?: (id: string) => void;
  timerActividadId?: string;
  isTimerActive?: boolean;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  subtareasMap: Record<string, ActividadListItem[]>;
  onLoadSubtareas: (id: string) => void;
  // Multi-select
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
}

// ─── Priority helpers ────────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  AAA: 'border-l-red-500',
  AA: 'border-l-orange-500',
  A: 'border-l-yellow-500',
  B: 'border-l-blue-400',
  C: 'border-l-gray-300',
};

const PRIORITY_RANK: Record<string, number> = { AAA: 0, AA: 1, A: 2, B: 3, C: 4 };

const AREA_LABELS: Record<string, { label: string; className: string }> = {
  TRABAJO: { label: 'Trabajo', className: 'text-blue-600 dark:text-blue-400' },
  ADMINISTRATIVO: { label: 'Admin', className: 'text-purple-600 dark:text-purple-400' },
  PERSONAL: { label: 'Personal', className: 'text-green-600 dark:text-green-400' },
};

// ─── Sortable Row ─────────────────────────────────────────────────────────────

export interface SortableRowProps {
  item: ActividadListItem;
  isSubtarea?: boolean;
  onComplete: (id: string) => void;
  onReopen: (id: string) => void;
  onStartTimer: (id: string) => void;
  onPauseTimer?: () => void;
  onResumeTimer?: () => void;
  isTimerPaused?: boolean;
  onToggleFoco?: () => void;
  onIndent?: () => void;
  onOutdent?: () => void;
  timerActividadId?: string;
  isTimerActive?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  hasSubtareas?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

export function SortableRow({
  item,
  isSubtarea = false,
  onComplete,
  onReopen,
  onStartTimer,
  onPauseTimer,
  onResumeTimer,
  isTimerPaused,
  onToggleFoco,
  onIndent,
  onOutdent,
  timerActividadId,
  isTimerActive,
  isExpanded,
  onToggleExpand,
  hasSubtareas,
  isSelected,
  onToggleSelect,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isCompleted = item.estado === 'COMPLETADA';
  const isCancelled = item.estado === 'CANCELADA';
  const isTimerOnThis = isTimerActive && timerActividadId === item.id;
  const canPlay = !isCompleted && !isCancelled;
  const area = AREA_LABELS[item.area];

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    // Don't intercept Tab inside inputs/textareas
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    if (e.shiftKey && onOutdent) {
      e.preventDefault();
      onOutdent();
    } else if (!e.shiftKey && onIndent) {
      e.preventDefault();
      onIndent();
    }
  }, [onIndent, onOutdent]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn(
        'group flex items-center gap-2 px-3 py-2 bg-background border-l-4 rounded-md border border-border/50 transition-all focus:outline-none focus:ring-1 focus:ring-primary/30',
        PRIORITY_COLORS[item.prioridad] || 'border-l-gray-300',
        isDragging && 'opacity-50 shadow-lg z-50',
        isSubtarea && 'ml-8',
        (isCompleted || isCancelled) && 'opacity-60',
        item.en_foco && 'ring-1 ring-amber-400/50 bg-amber-50/30 dark:bg-amber-950/20',
      )}
    >
      {/* Drag handle */}
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Selection checkbox */}
      {onToggleSelect && (
        <input
          type="checkbox"
          checked={isSelected ?? false}
          onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
          className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary/50 cursor-pointer shrink-0"
        />
      )}

      {/* Expand/collapse for parents with subtareas */}
      {hasSubtareas && !isSubtarea ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleExpand?.(); }}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      ) : isSubtarea ? (
        <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground/40" />
      ) : (
        <div className="w-4" />
      )}

      {/* Check button */}
      {isCompleted ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onReopen(item.id); }}
          className="shrink-0 h-5 w-5 rounded bg-green-500 hover:bg-green-400 flex items-center justify-center transition-colors"
          title="Reabrir actividad"
        >
          <Check className="h-3 w-3 text-white" />
        </button>
      ) : isCancelled ? (
        <div className="shrink-0 h-5 w-5 rounded bg-gray-300 flex items-center justify-center">
          <Check className="h-3 w-3 text-white" />
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onComplete(item.id); }}
          className="shrink-0 group/check h-5 w-5 rounded border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 flex items-center justify-center transition-colors"
        >
          <Check className="h-3 w-3 text-transparent group-hover/check:text-green-500 transition-colors" />
        </button>
      )}

      {/* Focus star */}
      {onToggleFoco && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFoco(); }}
          className={cn(
            'shrink-0 h-5 w-5 rounded flex items-center justify-center transition-colors',
            item.en_foco
              ? 'text-amber-500 hover:text-amber-400'
              : 'text-muted-foreground/20 hover:text-amber-400'
          )}
          title={item.en_foco ? 'Quitar enfoque' : 'Marcar como enfoque'}
        >
          <Star className={cn('h-3.5 w-3.5', item.en_foco && 'fill-current')} />
        </button>
      )}

      {/* Timer button (left side, next to check) */}
      {canPlay && isTimerOnThis && (onPauseTimer || onResumeTimer) ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isTimerPaused) {
              onResumeTimer?.();
            } else {
              onPauseTimer?.();
            }
          }}
          className="shrink-0 h-5 w-5 rounded flex items-center justify-center transition-colors text-primary bg-primary/10 animate-pulse"
          title={isTimerPaused ? 'Reanudar timer' : 'Pausar timer'}
        >
          {isTimerPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
        </button>
      ) : canPlay ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onStartTimer(item.id); }}
          className="shrink-0 h-5 w-5 rounded flex items-center justify-center transition-colors text-muted-foreground/40 hover:text-primary hover:bg-primary/10"
          title="Iniciar timer"
        >
          <Play className="h-3 w-3" />
        </button>
      ) : null}

      {/* Title + metadata */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Link
            to={`/actividades/${item.id}`}
            className={cn(
              'text-sm font-medium hover:text-primary truncate',
              (isCompleted || isCancelled) && 'line-through text-muted-foreground'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {item.titulo}
          </Link>
          {/* Parent indicator for subtasks shown independently in sections */}
          {!isSubtarea && item.padre_id && item.padre_titulo && (
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded shrink-0">
              <CornerDownRight className="h-2.5 w-2.5" />
              <span className="truncate max-w-[120px]">{item.padre_titulo}</span>
            </span>
          )}
          {item.es_recurrente && (
            <Repeat className="h-3 w-3 text-blue-500 shrink-0" />
          )}
          {!isSubtarea && (item.subtareas_count ?? 0) > 0 && !isExpanded && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 rounded-full">
              {item.subtareas_count}
            </span>
          )}
        </div>
        {/* Meta row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
          {item.tipo_actividad?.nombre && (
            <span>{item.tipo_actividad.nombre}</span>
          )}
          {item.cliente_nombre && (
            <span className="text-blue-600 dark:text-blue-400">{item.cliente_nombre}</span>
          )}
          {item.contacto_nombre && (
            <span className="text-green-600 dark:text-green-400">@{item.contacto_nombre}</span>
          )}
        </div>
      </div>

      {/* Estado badge */}
      <div className="shrink-0 hidden sm:block">
        <StatusBadge status={item.estado} />
      </div>

      {/* Prioridad badge */}
      <div className="shrink-0 hidden md:block">
        <PriorityBadge priority={item.prioridad} />
      </div>

      {/* Area */}
      {area && (
        <span className={cn('shrink-0 text-xs font-medium hidden lg:block', area.className)}>
          {area.label}
        </span>
      )}

      {/* Responsable */}
      {item.responsable_nombre && (
        <div className="shrink-0 hidden lg:flex items-center gap-1 text-xs text-muted-foreground" title={item.responsable_nombre}>
          <User className="h-3 w-3" />
          <span className="max-w-[80px] truncate">{item.responsable_nombre}</span>
        </div>
      )}

      {/* Date */}
      <div className="shrink-0 text-xs text-muted-foreground text-right min-w-[85px]">
        {item.fecha_agendada && (
          <div className="flex items-center gap-1 justify-end">
            <Calendar className="h-3 w-3" />
            {formatDate(item.fecha_agendada)}
          </div>
        )}
        {item.hora_agendada && (
          <div className="flex items-center gap-1 justify-end">
            <Clock className="h-3 w-3" />
            {item.hora_agendada.slice(0, 5)}
          </div>
        )}
      </div>

      {/* Indent / Outdent buttons */}
      {(onIndent || onOutdent) && (
        <div className="shrink-0 flex items-center gap-0.5">
          {onIndent && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onIndent(); }}
              className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/40 hover:text-primary hover:bg-primary/10 transition-colors"
              title="Convertir en subtarea de la actividad de arriba"
            >
              <Indent className="h-3.5 w-3.5" />
            </button>
          )}
          {onOutdent && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOutdent(); }}
              className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground/40 hover:text-primary hover:bg-primary/10 transition-colors"
              title="Sacar de subtarea (volver a root)"
            >
              <Outdent className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Drag overlay row (ghost) ─────────────────────────────────────────────────

export function DragOverlayRow({ item }: { item: ActividadListItem }) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-2 bg-background border-l-4 rounded-md border border-primary/30 shadow-xl',
      PRIORITY_COLORS[item.prioridad] || 'border-l-gray-300',
    )}>
      <GripVertical className="h-4 w-4 text-muted-foreground/40" />
      <div className="w-4" />
      <span className="text-sm font-medium truncate">{item.titulo}</span>
      <StatusBadge status={item.estado} />
      <PriorityBadge priority={item.prioridad} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ActividadDnDList({
  items,
  onReorder,
  onReparent,
  onComplete,
  onReopen,
  onStartTimer,
  onPauseTimer,
  onResumeTimer,
  isTimerPaused,
  onUpdatePriority,
  onToggleFoco,
  timerActividadId,
  isTimerActive,
  expandedIds,
  onToggleExpand,
  subtareasMap,
  onLoadSubtareas,
  selectedIds,
  onToggleSelect,
}: ActividadDnDListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Build the flat list of IDs for sortable context (parents + expanded subtareas)
  const flatIds = useMemo(() => {
    const ids: string[] = [];
    for (const item of items) {
      ids.push(item.id);
      if (expandedIds.has(item.id) && subtareasMap[item.id]) {
        for (const sub of subtareasMap[item.id]) {
          ids.push(sub.id);
        }
      }
    }
    return ids;
  }, [items, expandedIds, subtareasMap]);

  // Map all items by ID for quick lookup
  const allItemsMap = useMemo(() => {
    const map = new Map<string, ActividadListItem>();
    for (const item of items) {
      map.set(item.id, item);
      if (subtareasMap[item.id]) {
        for (const sub of subtareasMap[item.id]) {
          map.set(sub.id, sub);
        }
      }
    }
    return map;
  }, [items, subtareasMap]);

  const activeItem = activeId ? allItemsMap.get(activeId) : null;
  const selectedSet = useMemo(() => selectedIds ? new Set(selectedIds) : null, [selectedIds]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    // Reorder only
    const activeIdx = flatIds.indexOf(activeIdStr);
    const overIdx = flatIds.indexOf(overIdStr);
    if (activeIdx === -1 || overIdx === -1) return;

    // Build new order for affected items
    const reordered = [...flatIds];
    reordered.splice(activeIdx, 1);
    reordered.splice(overIdx, 0, activeIdStr);

    // Only send order updates for root items (subtareas keep their own order within parent)
    const reorderItems = reordered
      .filter((id) => {
        const item = allItemsMap.get(id);
        return item && !item.padre_id;
      })
      .map((id, index) => ({ id, orden: index }));

    onReorder(reorderItems);

    // Auto-adjust priority: if dropped between items of different priority,
    // adopt the priority of the next item below
    if (onUpdatePriority) {
      const draggedItem = allItemsMap.get(activeIdStr);
      if (!draggedItem) return;

      // Find neighbors in the new order (root items only)
      const rootReordered = reordered.filter((id) => {
        const item = allItemsMap.get(id);
        return item && !item.padre_id;
      });
      const newIdx = rootReordered.indexOf(activeIdStr);
      if (newIdx === -1) return;

      const nextItem = newIdx < rootReordered.length - 1 ? allItemsMap.get(rootReordered[newIdx + 1]) : null;
      const prevItem = newIdx > 0 ? allItemsMap.get(rootReordered[newIdx - 1]) : null;

      // Adopt the nearest neighbor's priority if the dragged item is lower priority.
      // Prefer the item below (nextItem); fall back to the item above (prevItem).
      const referenceItem = nextItem ?? prevItem;
      if (referenceItem) {
        const draggedRank = PRIORITY_RANK[draggedItem.prioridad] ?? 99;
        const referenceRank = PRIORITY_RANK[referenceItem.prioridad] ?? 99;
        if (draggedRank > referenceRank) {
          onUpdatePriority(activeIdStr, referenceItem.prioridad);
        }
      }
    }
  }, [flatIds, allItemsMap, onReorder, onUpdatePriority]);

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        No hay actividades
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {items.map((item, index) => {
            const hasSubtareas = (item.subtareas_count ?? 0) > 0;
            const isExpanded = expandedIds.has(item.id);
            const subtareas = subtareasMap[item.id] ?? [];
            // Can indent: any item (except first) can become a subtarea of the nearest root item above
            let prevRootItem: ActividadListItem | null = null;
            for (let j = index - 1; j >= 0; j--) {
              if (!items[j].padre_id) { prevRootItem = items[j]; break; }
            }
            const canIndent = prevRootItem !== null;

            return (
              <div key={item.id}>
                <SortableRow
                  item={item}
                  onComplete={onComplete}
                  onReopen={onReopen}
                  onStartTimer={onStartTimer}
                  onPauseTimer={onPauseTimer}
                  onResumeTimer={onResumeTimer}
                  isTimerPaused={isTimerPaused}
                  onToggleFoco={onToggleFoco ? () => onToggleFoco(item.id) : undefined}
                  timerActividadId={timerActividadId}
                  isTimerActive={isTimerActive}
                  isExpanded={isExpanded}
                  onToggleExpand={() => {
                    onToggleExpand(item.id);
                    if (!isExpanded && !subtareasMap[item.id]) {
                      onLoadSubtareas(item.id);
                    }
                  }}
                  hasSubtareas={hasSubtareas || isExpanded}
                  onIndent={canIndent ? () => onReparent(item.id, prevRootItem!.id) : undefined}
                  isSelected={selectedSet?.has(item.id)}
                  onToggleSelect={onToggleSelect ? () => onToggleSelect(item.id) : undefined}
                />
                {/* Subtareas + inline add */}
                {isExpanded && (
                  <>
                    {subtareas.map((sub) => (
                      <SortableRow
                        key={sub.id}
                        item={sub}
                        isSubtarea
                        onComplete={onComplete}
                        onReopen={onReopen}
                        onStartTimer={onStartTimer}
                        onPauseTimer={onPauseTimer}
                        onResumeTimer={onResumeTimer}
                        isTimerPaused={isTimerPaused}
                        onToggleFoco={onToggleFoco ? () => onToggleFoco(sub.id) : undefined}
                        timerActividadId={timerActividadId}
                        isTimerActive={isTimerActive}
                        onOutdent={() => onReparent(sub.id, null)}
                      />
                    ))}
                    <SubtareaInlineAdd
                      actividadPadreId={item.id}
                      onCreated={() => onLoadSubtareas(item.id)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </SortableContext>

      <DragOverlay>
        {activeItem ? <DragOverlayRow item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
